/**
 * Pure display helpers for StudentDetailModal (de-slop ADM-11). They return
 * i18n keys + interpolation values instead of English text, so the modal can
 * translate them and the logic stays unit-testable.
 */

export type ActivityTone = 'success' | 'warning' | 'neutral';

export interface ActivityStatus {
  tone: ActivityTone;
  key: string;
  vars?: Record<string, number>;
}

const DAY_MS = 1000 * 60 * 60 * 24;

export function lastActivityStatus(dateStr: string | null, now: number = Date.now()): ActivityStatus {
  if (!dateStr) return { tone: 'neutral', key: 'studentDetail.activity.none' };
  const days = Math.floor((now - new Date(dateStr).getTime()) / DAY_MS);
  if (days <= 1) return { tone: 'success', key: 'studentDetail.activity.today' };
  if (days <= 7) return { tone: 'success', key: 'studentDetail.activity.daysAgo', vars: { days } };
  if (days <= 30) return { tone: 'warning', key: 'studentDetail.activity.inactiveDays', vars: { days } };
  return { tone: 'warning', key: 'studentDetail.activity.inactiveLong' };
}

/** Picks the singular or plural key; the i18n layer has no plural rules. */
export function countKey(baseKey: string, count: number): string {
  return count === 1 ? `${baseKey}One` : `${baseKey}Other`;
}

/**
 * Analytics API competency keys -> the existing `icfProgress.competencies.*`
 * names, so the drawer reuses the translated ICF competency titles.
 */
export const COMPETENCY_LABEL_KEYS: Record<string, string> = {
  demonstrates_ethical_practice: 'icfProgress.competencies.ethicalPractice',
  embodies_coaching_mindset: 'icfProgress.competencies.coachingMindset',
  establishes_maintains_agreements: 'icfProgress.competencies.agreements',
  cultivates_trust_safety: 'icfProgress.competencies.trustSafety',
  maintains_presence: 'icfProgress.competencies.presence',
  listens_actively: 'icfProgress.competencies.activeListening',
  evokes_awareness: 'icfProgress.competencies.awareness',
  facilitates_client_growth: 'icfProgress.competencies.clientGrowth',
};

/** BCP 47 tag for date formatting in the UI language. */
export function dateLocaleFor(language: string): string {
  return language === 'tr' ? 'tr-TR' : 'en-US';
}
